import React, { useCallback, useRef, useState, useMemo, useEffect } from 'react';
import _ from 'underscore';
import {
  ColumnSection,
  Container,
  FieldTitle,
  RowSection,
  PaddingOption,
  BlockColor,
  ComplexBlockSettingsTabsContainer,
  StyledTabPane,
  ButtonWrapper,
  TabPaneBody,
} from './styles';
import { Button, Select, Input, InputNumber, Popconfirm, Switch, message, Tooltip, Icon, Modal, Tabs } from 'antd';
import ColorPicker from '../../artboard/ColorPicker';
import { BLOCK_SETTINGS_OPTIONS, BLOCK_SETTINGS_TABS } from '../../containers/ComplexDocumentDesign/translationValues';
import { useTranslation } from 'react-i18next';
import {
  FOOTER,
  HEADER,
  TEXT_BLOCK,
  CREATIVE_BLOCK,
  DYNAMIC_TABLE_BLOCK,
  TEXT_DEBOUNCE_TIME_OUT,
  LINK_BLOCK,
  BUTTON_BLOCK,
  FILLABLE_ELEMENT,
  HTML_BLOCK,
} from '../../utils/complexDocumentConstants';
import { useDocumentContext, COMPLEX_DOCUMENT_CONTEXT_KEYS } from '../../context/complexDocumentContext';
import { deepCopy, measurementConversion } from '../../utils/javascript';
import Regex from '../../utils/regex';
import { updateResourceNames } from '../../utils/services/complexDocumentsAPI';
import { CONTEXT_KEYS, useArtboardContext } from '../../context/complexArtboardContext';
import { updateEmailResourceNames } from '../../utils/services/complexEmailAPI';
import styled from 'styled-components';
import { VARIABLE_TYPE } from '../../utils/constants/appConstants';
import { SettingsHeading, TabMenuContainer } from '../../containers/SidebarMenu/styles';
import AccessibilitySummaryEditor from '../../containers/SidebarMenu/AccessibilitySummaryEditor';
import { ENTERPRISE_ENV } from '../../utils/helper';

const NAMESPACES = ['BLOCK_SETTINGS_OPTIONS'];
const { Option } = Select;

const GLOBAL_REGEX_FOR_BLOCK_NAME_TO_VARIABLE_NAME = /[`!@#$%^&*()_+=\[\]{};':"\\|,.<>\/?~\s]/g;

const IconWrapper = styled.div`
    background-color: ${props => props.selected ? 'grey' : 'white'};
    height: 40px;
    width: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
`;

const AlignmentWrapper = styled.div`
  display: flex;
  column-gap: 10px;
`;

const ComplexBlockSettings = props => {
  const { methods, selectedItem, closeSideBar, editable } = props;
  const { pageId, item, itemUUID, parentUUID } = selectedItem;
  const blockTypeSelector = useCallback(
    store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.REUSABLE_BLOCK_TYPE],
    [],
  );
  const [reusableBlockType] = useDocumentContext(blockTypeSelector);
  const customSelector = useCallback(store => store[itemUUID], [itemUUID]);
  const [currentBlock] = useDocumentContext(customSelector, `Block-${itemUUID}`);
  const currentDesignSelector = useCallback((store) => store[CONTEXT_KEYS.CURRENT_DESIGN], []);
  const [currentDesign] = useArtboardContext(currentDesignSelector, '');
  const isEmailSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.IS_EMAIL_TEMPLATE], []);
  const [isEmailTemplate] = useDocumentContext(isEmailSelector, `Block-${itemUUID}`);
  const interactiveDesignSelector = useCallback(store => store[CONTEXT_KEYS.IS_INTERACTIVE_DESIGN], []);
  const [isInteractiveDesign] = useArtboardContext(interactiveDesignSelector, `ComplexBlockSettings`);
  const isOpenDesignSelector = useCallback(store => store[CONTEXT_KEYS.IS_OPEN_DESIGN], []);
  const [isOpenDesign] = useArtboardContext(isOpenDesignSelector, `ComplexBlockSettings`);
  const activeTabSelector = useCallback(store => store[CONTEXT_KEYS.BLOCK_SETTINGS_ACTIVE_TAB], []);
  const [activeTab, updateArtboard] = useArtboardContext(activeTabSelector, `ComplexBlockSettings`);

  const [applyAllSides, toggleApplyAllSides] = useState(true);
  const [picker, setPicker] = useState(false);
  const [measurement, setMeasurement] = useState(isEmailTemplate ? 'px' : 'mm');
  const [changedPadding, setChangedPadding] = useState({});
  const [blockName, setBlockName] = useState('');
  const [showFillableModal, setShowFillableModal] = useState();
  const [currentActiveTab, setCurrentActiveTab] = useState(() => activeTab);
  const fieldNameRef = useRef(null);
  const variableNameRef = useRef(null);
  const forwardRef = useRef(null);
  const measurementRef = useRef(measurement);
  const { t } = useTranslation(NAMESPACES);
  const designEditableSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.IS_EDITABLE], []);
  const [isCloneable] = useDocumentContext(designEditableSelector, ``);

  const isRepetitive = currentBlock?.variableContract
    ? currentBlock?.variableContract[0]?.type === 'object'
      && (currentBlock.blockType === DYNAMIC_TABLE_BLOCK ? currentBlock.variableContract[0]?.isArray : true)
    : false;
  const isHtml = currentBlock?.blockType === HTML_BLOCK ? true : false;
  const isEditable = currentBlock?.isEditable;
  useEffect(() => {
    setBlockName(currentBlock.blockName);
  }, [currentBlock.uuid]);

  useEffect(() => {
    forwardRef.current = true;
  }, [itemUUID, currentBlock]);

  useEffect(() => {
    let currentItem = currentBlock;
    if (currentItem?.style?.padding) {
      let itemPadding = {};
      if (measurement === 'px') {
        itemPadding = {
          left: currentItem?.style?.padding?.left,
          right: currentItem?.style?.padding?.right,
          top: currentItem?.style?.padding?.top,
          bottom: currentItem?.style?.padding?.bottom,
        };
      } else {
        itemPadding = {
          left: measurementConversion(currentItem?.style?.padding?.left, 'mm'),
          right: measurementConversion(currentItem?.style?.padding?.right, 'mm'),
          top: measurementConversion(currentItem?.style?.padding?.top, 'mm'),
          bottom: measurementConversion(currentItem?.style?.padding?.bottom, 'mm'),
        };
      }
      setChangedPadding(itemPadding);
      toggleApplyAllSides(
        itemPadding.top === itemPadding.bottom &&
          itemPadding.top === itemPadding.right &&
          itemPadding.top === itemPadding.left,
      );
    }
    if (forwardRef.current === true && fieldNameRef.current !== null) {
      forwardRef.current = false;
      fieldNameRef.current.setState({ value: currentBlock.blockName || '' });
      if (variableNameRef.current !== null) {
        if (
          currentBlock.variableContract?.length > 0 &&
          (currentBlock.variableContract[0].type === VARIABLE_TYPE.OBJECT || currentBlock.variableContract[0].type === VARIABLE_TYPE.HTML)
        ) {
          variableNameRef.current.setState({ value: currentBlock.variableContract[0].name });
        } else {
          variableNameRef.current.setState({ value: '' });
        }
      }
    }
  }, [currentBlock]);

  useEffect(() => {
    if (item?.style?.padding) {
      let itemPadding = {
          left: item?.style?.padding?.left,
          right: item?.style?.padding?.right,
          top: item?.style?.padding?.top,
          bottom: item?.style?.padding?.bottom,
        };
      if (measurement === 'mm') {
        itemPadding = {
          left: measurementConversion(item?.style?.padding?.left, 'mm'),
          right: measurementConversion(item?.style?.padding?.right, 'mm'),
          top: measurementConversion(item?.style?.padding?.top, 'mm'),
          bottom: measurementConversion(item?.style?.padding?.bottom, 'mm'),
        };
      }
      setChangedPadding(itemPadding);
      toggleApplyAllSides(
        itemPadding.top === itemPadding.bottom &&
          itemPadding.top === itemPadding.right &&
          itemPadding.top === itemPadding.left,
      );
    } else {
      setChangedPadding({
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
      });
    }
  }, [item]);

  useEffect(() => {
    if (measurementRef.current !== measurement) {
      measurementRef.current = measurement;
      setChangedPadding(existingPadding => {
        const newPadding = {
          left: measurementConversion(existingPadding?.left, measurement),
          right: measurementConversion(existingPadding?.right, measurement),
          top: measurementConversion(existingPadding?.top, measurement),
          bottom: measurementConversion(existingPadding?.bottom, measurement),
        };
        return newPadding;
      });
    }
  }, [measurement]);

  const showBackgroundColorPicker = useCallback(() => {
    setPicker(currentValue => {
      if (currentValue && currentValue.type === 'backgroundColor') {
        return false;
      }
      return { type: 'backgroundColor', color: item?.style?.backgroundColor };
    });
  }, [item]);

  const onColorPick = useCallback(
    color => {
      methods.updateBlock('backgroundColor', itemUUID, {
        backgroundColor: color.hex,
        flowUUID: parentUUID,
        type: item.type,
      });
    },
    [picker, itemUUID, item],
  );

  const onAlignmentChange = useCallback(
    alignment => {
      methods.updateBlock('alignment', itemUUID, {
        alignment: alignment,
        flowUUID: parentUUID,
        type: item.type,
      });
    },
    [picker, itemUUID, item],
  );

  const onDeleteBlockClick = useCallback(() => {
    let deleteItem = item;
    if (deleteItem) {
      let isStaticElement = deleteItem.type === HEADER || deleteItem.type === FOOTER;
      if (isStaticElement) {
        methods.setPage(
          'deleteStaticElement',
          {
            ...deleteItem,
            uuid: parentUUID,
          },
          true,
        );
      } else if (deleteItem.isChild) {
        methods.updateBlock('deleteChildBlock', deleteItem.parentBlockUUID, {
          block: deleteItem,
          pageId,
          flowUUID: parentUUID,
        });
      } else {
        methods.setPage(
          'deleteBlock',
          {
            parentUUID,
            block: deleteItem,
            pageId,
            flowUUID: parentUUID,
            uuid: parentUUID,
          },
          true,
        );
      }
    }
    closeSideBar();
  }, [item]);

  const changeBlockPadding = useCallback(
    (val, direction) => {
      let blockPadding = changedPadding;
      let changedValue = undefined;
      if (measurement === 'px') {
        changedValue = Math.round(val);
      } else {
        changedValue = measurementConversion(Math.round(val), 'px');
      }
      if (direction === 'all') {
        toggleApplyAllSides(true);
        blockPadding = {
          left: changedValue,
          right: changedValue,
          top: changedValue,
          bottom: changedValue,
        };
      } else {
        toggleApplyAllSides(false);
        blockPadding = {
          right: measurement === 'px' ? blockPadding.right : measurementConversion(Math.round(blockPadding.right), 'px'),
          left: measurement === 'px' ? blockPadding.left : measurementConversion(Math.round(blockPadding.left), 'px'),
          top: measurement === 'px' ? blockPadding.top : measurementConversion(Math.round(blockPadding.top), 'px'),
          bottom: measurement === 'px' ? blockPadding.bottom : measurementConversion(Math.round(blockPadding.bottom), 'px'),
          [direction]: changedValue,
        };
      }
      methods.updateBlock('padding', itemUUID, {
        padding: blockPadding,
        flowUUID: parentUUID,
        type: item.type,
      });
    },
    [item, measurement, itemUUID, changedPadding],
  );

  const incOrDecPadding = (val, direction) => {
    if (direction === 'all') {
      setChangedPadding({
        left: val,
        right: val,
        top: val,
        bottom: val,
      });
    } else {
      setChangedPadding({ ...changedPadding, [direction]: val });
    }
  };

  const updateBlockRepetition = useCallback(
    (checked, variableName = '', contents) => {
      if (isHtml) {
        methods.updateBlock('html', itemUUID, {
          variableName: typeof variableName === 'string' ? variableName : undefined,
          flowUUID: parentUUID,
        });
      }
      else {
        methods.updateBlock('repetitive', itemUUID, {
          repeat: checked,
          variableName: typeof variableName === 'string' ? variableName : undefined,
          contents: contents,
          flowUUID: parentUUID,
        });
      }
    },
    [parentUUID, itemUUID, isRepetitive, isHtml],
  );

  const updateBlockName = useCallback(
    async input => {
      const name = input.trim();
      const blockId = methods.get().backendIds[itemUUID];
      // Trigger the API call only if the input is not empty
      if(name.length > 0) {
        let response;
        if (!isOpenDesign) {
          if (isEmailTemplate) {
            response = await updateEmailResourceNames({ blockId, name }, currentDesign === 'block');
          } else {
            response = await updateResourceNames({ blockId, name }, currentDesign === 'block');
          }
        }
        if(response?.statusCode === 1804 || isOpenDesign) {
          methods.updateBlock('name', itemUUID, {
            name: name,
            flowUUID: parentUUID,
          }, isOpenDesign ? true : false);
        } else {
            message.error(t(BLOCK_SETTINGS_OPTIONS.BLOCK_NAME_UPDATE_FAILED_MESSAGE.key, BLOCK_SETTINGS_OPTIONS.BLOCK_NAME_UPDATE_FAILED_MESSAGE.value));
        }
      }
    },
    [parentUUID, itemUUID],
  );

  const debouncedUpdateBlockName = useMemo(() => {
    return _.debounce(updateBlockName, TEXT_DEBOUNCE_TIME_OUT);
  }, [updateBlockName]);
  const debouncedUpdateRepetition = useMemo(() => {
    return _.debounce(updateBlockRepetition, TEXT_DEBOUNCE_TIME_OUT);
  }, [updateBlockRepetition]);

  const onNameChange = useCallback(
    e => {
      setBlockName(e.target.value);
      debouncedUpdateBlockName(e.target.value);
    },
    [debouncedUpdateBlockName],
  );

  const handleBlur = (e) => {
    // Display the previous name when the input is blurred and empty
    const inputLength = e.target.value.trim().length;
    if (inputLength < 1) {
      message.warn(t(BLOCK_SETTINGS_OPTIONS.BLOCK_NAME_EMPTY_WARNING_MESSAGE.key, BLOCK_SETTINGS_OPTIONS.BLOCK_NAME_EMPTY_WARNING_MESSAGE.value));
      e.target.value = currentBlock.blockName || '';
    }
  };

  const onRepetitiveChange = useCallback(
    checked => {
      if (checked) { // TODO : Check if same code works for static table (not applicable for dynamic table)
        const hasFillableElements = currentBlock?.contents.some(
          el => el?.contentType === FILLABLE_ELEMENT,
        );
        if (hasFillableElements) {
          setShowFillableModal(true);
          return;
        }
      }
      updateBlockRepetition(
        checked,
        checked && currentBlock.blockName
          ? currentBlock.blockName.replaceAll(GLOBAL_REGEX_FOR_BLOCK_NAME_TO_VARIABLE_NAME, '-')
          : undefined,
      );
    },
    [updateBlockRepetition, currentBlock],
  );

  const onFillableModalCancel = () => {
    setShowFillableModal(false);
  };

  const onFillableModalConfirm = () => {
    const currentContents = deepCopy(
      currentBlock?.contents?.filter(el => el.contentType !== FILLABLE_ELEMENT),
    );
    updateBlockRepetition(
      true,
      currentBlock.blockName
        ? currentBlock.blockName.replaceAll(GLOBAL_REGEX_FOR_BLOCK_NAME_TO_VARIABLE_NAME, '-')
        : undefined,
      currentContents,
    );
    setShowFillableModal(false);
  };

  const onVariableNameChange = useCallback(
    e => {
      if (Regex.NO_SPLCHAR_EXCEPT_HYPHEN.test(e.target.value)) {
        message.error(
          t(
            BLOCK_SETTINGS_OPTIONS.VARIABLE_NAME_SPECIAL_CHARACTER_ERROR_MESSAGE.key,
            BLOCK_SETTINGS_OPTIONS.VARIABLE_NAME_SPECIAL_CHARACTER_ERROR_MESSAGE.value,
          ),
        );
        return;
      } else {
        debouncedUpdateRepetition(true, e.target.value);
      }
    },
    [debouncedUpdateRepetition],
  );

  const onEditableChange = useCallback(
    (checked) => {
      methods.updateBlock('isEditable', itemUUID, {
        isEditable: checked,
        flowUUID: parentUUID,
        type: item.type,
      })
    },
    [parentUUID, itemUUID],
  );

  const showPaddingOPtions = useCallback(() => {
    return (
      <ColumnSection>
        <RowSection>
          <FieldTitle isStyleChange={1}>
            {t(
              BLOCK_SETTINGS_OPTIONS.ADD_SPACE_TO_SECTION_LABEL.key,
              BLOCK_SETTINGS_OPTIONS.ADD_SPACE_TO_SECTION_LABEL.value,
            )}
          </FieldTitle>
          <Select value={measurement} style={{ width: 75 }} onChange={e => setMeasurement(e)} disabled={isEmailTemplate}>
            {!isEmailTemplate && (<Option value="mm">mm</Option>)}
            <Option value="px">px</Option>
          </Select>
        </RowSection>
        <PaddingOption>
          <FieldTitle isSpacing={1}>
            {t(
              BLOCK_SETTINGS_OPTIONS.ALL_SIDES_SPACING_LABEL.key,
              BLOCK_SETTINGS_OPTIONS.ALL_SIDES_SPACING_LABEL.value,
            )}
            <Switch checked={applyAllSides} />
          </FieldTitle>
          <InputNumber
            min={0}
            value={
              changedPadding.top === changedPadding.bottom &&
              changedPadding.top === changedPadding.right &&
              changedPadding.top === changedPadding.left
                ? changedPadding.top
                : undefined
            }
            onChange={e => incOrDecPadding(e, 'all')}
            onPressEnter={e => changeBlockPadding(parseInt(e?.target?.value), 'all')}
            disabled={!editable}
          />
        </PaddingOption>
        <PaddingOption>
          <FieldTitle isSpacing={1}>
            {t(
              BLOCK_SETTINGS_OPTIONS.TOP_SPACING_LABEL.key,
              BLOCK_SETTINGS_OPTIONS.TOP_SPACING_LABEL.value,
            )}
          </FieldTitle>
          <InputNumber
            min={0}
            value={changedPadding.top}
            onChange={e => incOrDecPadding(e, 'top')}
            onPressEnter={e => changeBlockPadding(parseInt(e?.target?.value), 'top')}
            disabled={!editable}
          />
        </PaddingOption>
        <PaddingOption>
          <FieldTitle isSpacing={1}>
            {t(
              BLOCK_SETTINGS_OPTIONS.BOTTOM_SPACING_LABEL.key,
              BLOCK_SETTINGS_OPTIONS.BOTTOM_SPACING_LABEL.value,
            )}
          </FieldTitle>
          <InputNumber
            min={0}
            value={changedPadding.bottom}
            onChange={e => incOrDecPadding(e, 'bottom')}
            onPressEnter={e => changeBlockPadding(parseInt(e?.target?.value), 'bottom')}
            disabled={!editable}
          />
        </PaddingOption>
        <PaddingOption>
          <FieldTitle isSpacing={1}>
            {t(
              BLOCK_SETTINGS_OPTIONS.RIGHT_SPACING_LABEL.key,
              BLOCK_SETTINGS_OPTIONS.RIGHT_SPACING_LABEL.value,
            )}
          </FieldTitle>
          <InputNumber
            min={0}
            value={changedPadding.right}
            onChange={e => incOrDecPadding(e, 'right')}
            onPressEnter={e => changeBlockPadding(parseInt(e?.target?.value), 'right')}
            disabled={!editable}
          />
        </PaddingOption>
        <PaddingOption>
          <FieldTitle isSpacing={1}>
            {t(
              BLOCK_SETTINGS_OPTIONS.LEFT_SPACING_LABEL.key,
              BLOCK_SETTINGS_OPTIONS.LEFT_SPACING_LABEL.value,
            )}
          </FieldTitle>
          <InputNumber
            min={0}
            value={changedPadding.left}
            onChange={e => incOrDecPadding(e, 'left')}
            onPressEnter={e => changeBlockPadding(parseInt(e?.target?.value), 'left')}
            disabled={!editable}
          />
        </PaddingOption>
      </ColumnSection>
    );
  }, [changedPadding, measurement, applyAllSides, editable]);

  if (!currentBlock) {
    return <div />
  }

  const isHeaderOrFooterBlock = [HEADER, FOOTER].includes(item.type);
  const isHeaderOrFooterReusableBlock = [HEADER, FOOTER].includes(reusableBlockType);
  const linkOrButtonBlock = [LINK_BLOCK, BUTTON_BLOCK].includes(item.blockType);
  const { alignment } = currentBlock.style;
  const { TabPane } = Tabs;

  const onBlockSummarySave = changes => {
    const details = {
      summary: changes.contents,
      flowUUID: parentUUID,
      type: isHeaderOrFooterBlock ? item.type : undefined,
    };
    methods.updateBlock('summary', itemUUID, details);
  };
  
  const onBlockAccessibleSave = checked => {
    methods.updateBlock('style', itemUUID, {
      style: { accessible: checked },
      flowUUID: parentUUID,
      type: isHeaderOrFooterBlock ? item.type : undefined,
    });
  };

  const handleTabChange = (key) => {
    setCurrentActiveTab(key);
  }

  const isBlockRepeatable = item?.blockType !== HTML_BLOCK && (isEmailTemplate ? item?.blockType !== DYNAMIC_TABLE_BLOCK  : true);

  return (
    <ComplexBlockSettingsTabsContainer>
      <Tabs tabBarStyle={{ color: 'white' }} onChange={handleTabChange} activeKey={currentActiveTab}>
        <TabPane tab={t(
          BLOCK_SETTINGS_TABS.BLOCK_SETTINGS_TAB_SETTINGS.key,
          BLOCK_SETTINGS_TABS.BLOCK_SETTINGS_TAB_SETTINGS.value,
        )} key="1">
          <StyledTabPane disabled={isInteractiveDesign && !currentBlock?.isEditable}>
            <TabPaneBody>
              <ColumnSection>
                <FieldTitle>
                  {t(
                    BLOCK_SETTINGS_OPTIONS.SECTION_NAME_LABEL.key,
                    BLOCK_SETTINGS_OPTIONS.SECTION_NAME_LABEL.value,
                  )}
                </FieldTitle>
                <Input
                  ref={fieldNameRef} // TODO : refresh on change of current block
                  value={blockName}
                  placeholder={t(
                    BLOCK_SETTINGS_OPTIONS.SECTION_NAME_PLACEHOLDER.key,
                    BLOCK_SETTINGS_OPTIONS.SECTION_NAME_PLACEHOLDER.value,
                  )}
                  onChange={onNameChange}
                  onBlur={handleBlur}
                  disabled={!editable}
                />
              </ColumnSection>
              {item?.blockType !== CREATIVE_BLOCK && showPaddingOPtions()}
              {(!item.isChild && !isHeaderOrFooterBlock && !isHeaderOrFooterReusableBlock && isBlockRepeatable) && (
                <FieldTitle isStyleChange={1} style={{ paddingLeft: 20, paddingRight: 20 }}>
                  {t(
                    BLOCK_SETTINGS_OPTIONS.REPEAT_BLOCK_LABEL.key,
                    BLOCK_SETTINGS_OPTIONS.REPEAT_BLOCK_LABEL.value,
                  )}
                  <Tooltip
                    title={currentBlock.variableContract.length === 0 ? t(
                      BLOCK_SETTINGS_OPTIONS.REPEAT_ONLY_WITH_VARIABLES_TOOLTIP.key,
                      BLOCK_SETTINGS_OPTIONS.REPEAT_ONLY_WITH_VARIABLES_TOOLTIP.value,
                    ) : t(
                      BLOCK_SETTINGS_OPTIONS.REPEAT_BLOCK_LABEL.key,
                      BLOCK_SETTINGS_OPTIONS.REPEAT_BLOCK_LABEL.value,
                    )}
                  >
                    <Switch
                      /*checkedChildren={t(CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.DYNAMIC_VALUE_YES_SWITCH.key, CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.DYNAMIC_VALUE_YES_SWITCH.value)}
                    unCheckedChildren={t(CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.DYNAMIC_VALUE_NO_SWITCH.key, CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.DYNAMIC_VALUE_NO_SWITCH.value)}*/
                      checked={isRepetitive}
                      disabled={!editable || currentBlock.variableContract.length === 0 || isInteractiveDesign} // TODO : Add tooltip for being disabled
                      onChange={onRepetitiveChange}
                    />
                  </Tooltip>
                </FieldTitle>
              )}
              {ENTERPRISE_ENV && isCloneable && !isInteractiveDesign && !isEmailTemplate && (!item.isChild && !isHeaderOrFooterBlock && !isHeaderOrFooterReusableBlock && item?.blockType === TEXT_BLOCK) ? (
                <FieldTitle isStyleChange={1} style={{ paddingLeft: 20, paddingRight: 20 }}>
                  {t(
                    BLOCK_SETTINGS_OPTIONS.EDITABLE_LABEL.key,
                    BLOCK_SETTINGS_OPTIONS.EDITABLE_LABEL.value,
                  )}
                  <Tooltip
                    title={t(
                      BLOCK_SETTINGS_OPTIONS.EDITABLE_TOOLTIP.key,
                      BLOCK_SETTINGS_OPTIONS.EDITABLE_TOOLTIP.value,
                    )}
                  >
                    <Switch
                      checked={isEditable}
                      onChange={onEditableChange}
                    />
                  </Tooltip>
                </FieldTitle>
              ) : null}
              {(isRepetitive || isHtml) && (
                <ColumnSection>
                  <FieldTitle>
                    {t(
                      BLOCK_SETTINGS_OPTIONS.VARIABLE_NAME_LABEL.key,
                      BLOCK_SETTINGS_OPTIONS.VARIABLE_NAME_LABEL.value,
                    )}
                  </FieldTitle>
                  <Input
                    ref={variableNameRef} // TODO : refresh on change of current block
                    defaultValue={currentBlock.variableContract[0]?.name}
                    placeholder={t(BLOCK_SETTINGS_OPTIONS.VARIABLE_NAME_LABEL.key, BLOCK_SETTINGS_OPTIONS.VARIABLE_NAME_LABEL.value)}
                    onChange={onVariableNameChange}
                    disabled={!editable}
                  />
                </ColumnSection>
              )}
              <ColumnSection>
                <RowSection>
                  <FieldTitle isStyleChange={1}>
                    {t(
                      BLOCK_SETTINGS_OPTIONS.BLOCK_BACKGROUND_LABEL.key,
                      BLOCK_SETTINGS_OPTIONS.BLOCK_BACKGROUND_LABEL.value,
                    )}
                  </FieldTitle>
                  <BlockColor
                    background={currentBlock?.style?.backgroundColor}
                    onClick={showBackgroundColorPicker}
                    disabled={!editable}
                  />
                  {picker && (
                    <div style={{ minHeight: '228px' }}>
                      <div
                        style={{
                          position: 'absolute',
                          // bottom: (item?.blockType === TEXT_BLOCK || item?.blockType === DYNAMIC_TABLE_BLOCK) && '50px',
                          top: item?.blockType !== TEXT_BLOCK && item?.blockType !== DYNAMIC_TABLE_BLOCK && '0',
                          right: '75px',
                          zIndex: 11
                        }}
                      >
                        <ColorPicker
                          onChange={onColorPick}
                          onBlur={() => setPicker(false)}
                          color={currentBlock?.style?.backgroundColor}
                          qlEditor={true}
                        />
                      </div>
                    </div>
                  )}
                </RowSection>
              </ColumnSection>

              {linkOrButtonBlock && <ColumnSection>
                <RowSection>
                  <FieldTitle isStyleChange={1} style={{ width: '50%' }}>
                    {t(
                      BLOCK_SETTINGS_OPTIONS.BLOCK_ALIGNMENT_LABEL.key,
                      BLOCK_SETTINGS_OPTIONS.BLOCK_ALIGNMENT_LABEL.value,
                    )}
                  </FieldTitle>
                  <AlignmentWrapper>
                    <IconWrapper selected={alignment === 'left'} onClick={() => onAlignmentChange('left')}>
                      <Icon type="align-left" />
                    </IconWrapper>
                    <IconWrapper selected={alignment === 'center'} onClick={() => onAlignmentChange('center')}>
                      <Icon type="align-center" />
                    </IconWrapper>
                    <IconWrapper selected={alignment === 'right'} onClick={() => onAlignmentChange('right')}>
                      <Icon type="align-right" />
                    </IconWrapper>
                  </AlignmentWrapper>
                </RowSection>
              </ColumnSection>}
            {editable && (<Popconfirm
              title={t(
                BLOCK_SETTINGS_OPTIONS.DELETE_BLOCK_CONFIRMATION_POPUP_LABEL.key,
                BLOCK_SETTINGS_OPTIONS.DELETE_BLOCK_CONFIRMATION_POPUP_LABEL.value,
              )}
              okText={t(
                BLOCK_SETTINGS_OPTIONS.DELETE_BLOCK_CONFIRMATION_YES_BUTTON.key,
                BLOCK_SETTINGS_OPTIONS.DELETE_BLOCK_CONFIRMATION_YES_BUTTON.value,
              )}
              cancelText={t(
                BLOCK_SETTINGS_OPTIONS.DELETE_BLOCK_CONFIRMATION_NO_BUTTON.key,
                BLOCK_SETTINGS_OPTIONS.DELETE_BLOCK_CONFIRMATION_NO_BUTTON.value,
              )}
              placement="left"
              trigger="click"
              onConfirm={() => {
                onDeleteBlockClick();
              }}
              overlayStyle={{ width: 350 }}
            >
              <ButtonWrapper>
                <Button className="deleteButton">
                  {t(
                    BLOCK_SETTINGS_OPTIONS.DELETE_BLOCK_TOOLTIP.key,
                    BLOCK_SETTINGS_OPTIONS.DELETE_BLOCK_TOOLTIP.value,
                  )}
                </Button>
              </ButtonWrapper>
            </Popconfirm>)}
            </TabPaneBody>
            {showFillableModal && (
              <Modal
                width="600px"
                centered
                title={null}
                maskClosable={false}
                visible={!!showFillableModal}
                onCancel={onFillableModalCancel}
                footer={[
                  <Button onClick={onFillableModalCancel}>
                    {t(
                      BLOCK_SETTINGS_OPTIONS.DELETE_BLOCK_CONFIRMATION_NO_BUTTON.key,
                      BLOCK_SETTINGS_OPTIONS.DELETE_BLOCK_CONFIRMATION_NO_BUTTON.value,
                    )}
                  </Button>,
                  <Button type="primary" onClick={onFillableModalConfirm}>
                    {t(
                      BLOCK_SETTINGS_OPTIONS.DELETE_BLOCK_CONFIRMATION_YES_BUTTON.key,
                      BLOCK_SETTINGS_OPTIONS.DELETE_BLOCK_CONFIRMATION_YES_BUTTON.value,
                    )}
                  </Button>,
                ]}
                keyboard={false}
              >
                <div style={{ fontSize: '18px', margin: '20px 0px' }}>
                  Repeat block cannot contain form elements. Form elements will be removed. Would you like to proceed?
                </div>
              </Modal>
            )}
          </StyledTabPane>
        </TabPane>
        <TabPane 
          tab={<>{t(BLOCK_SETTINGS_OPTIONS.ACCESSIBILITY_TAB.key, BLOCK_SETTINGS_OPTIONS.ACCESSIBILITY_TAB.value)}{" "}
            <Tooltip
              placement="top"
              title={t(BLOCK_SETTINGS_OPTIONS.SCREEN_READER_SETTINGS_TOOLTIP.key, BLOCK_SETTINGS_OPTIONS.SCREEN_READER_SETTINGS_TOOLTIP.value)}>
              <Icon type="question-circle" />
            </Tooltip></>}
          key="2">
          {isHeaderOrFooterBlock && <>
          <ColumnSection>
            <p className="note-text">{t(BLOCK_SETTINGS_OPTIONS.HEADER_FOOTER_SUMMARY_NOTE.key, BLOCK_SETTINGS_OPTIONS.HEADER_FOOTER_SUMMARY_NOTE.value)}</p>
          </ColumnSection> 
          </>}    
          <ColumnSection disabled={isInteractiveDesign}>
            <SettingsHeading complexDesign>
              {t(BLOCK_SETTINGS_OPTIONS.SECTION_SUMMARY_LABEL.key, BLOCK_SETTINGS_OPTIONS.SECTION_SUMMARY_LABEL.value)}{' '}
            </SettingsHeading>
            <TabMenuContainer complexDesign style={{ width: '100%' }}>
              <AccessibilitySummaryEditor
                key={itemUUID}
                itemUUID={`accessibility-summary-${itemUUID}`}
                contents={currentBlock?.style?.summary}
                customContainerStyle={{ marginTop: 10 }}
                onSaveChanges={onBlockSummarySave}
                customizeHistory={false}
                customClassName={'summary-editor'}
              />
            </TabMenuContainer>
          </ColumnSection>
        </TabPane>
      </Tabs>

    </ComplexBlockSettingsTabsContainer>

  );
};

export default React.memo(ComplexBlockSettings);
